import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MessageSquare, Search, ArrowLeft, User, Bot, X, Filter, UserCheck } from 'lucide-react';
import api from '../services/api';

const DATE_OPTIONS = [
    { value: '', label: 'Qualquer data' },
    { value: 'hoje', label: 'Hoje' },
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: 'mes', label: 'Este mês' },
    { value: 'custom', label: 'Personalizado' },
];

export default function Conversas() {
    const [threads, setThreads] = useState([]);
    const [sel, setSel] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState(null);
    const selRef = useRef(null);
    const msgsEndRef = useRef(null);

    const [fFunc, setFFunc] = useState('');
    const [fCliente, setFCliente] = useState('');
    const [fData, setFData] = useState('');
    const [dtDe, setDtDe] = useState('');
    const [dtAte, setDtAte] = useState('');

    useEffect(() => { loadThreads(); }, []);

    // SSE — escutar eventos em tempo real
    useEffect(() => {
        const token = localStorage.getItem('synapse_token');
        if (!token) return;
        const es = new EventSource('/api/conversas/stream?token=' + token);
        es.addEventListener('nova_mensagem', (e) => {
            try {
                const data = JSON.parse(e.data);
                loadThreads();
                const cur = selRef.current;
                if (cur && data.telefone === cur.telefone_contato && data.funcionario_id === cur.funcionario_id) {
                    loadMsgs(cur);
                }
            } catch(err) {}
        });
        es.onerror = () => {
            es.close();
            setTimeout(() => {
                // reconectar em 5s
                const retry = new EventSource('/api/conversas/stream?token=' + token);
                retry.addEventListener('nova_mensagem', () => loadThreads());
            }, 5000);
        };
        return () => es.close();
    }, []);

    // manter selRef sincronizado
    useEffect(() => { selRef.current = sel; }, [sel]);

    // scroll automatico ao receber msgs novas
    useEffect(() => {
        if (msgsEndRef.current) msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [msgs]);

    const loadThreads = async () => { try { const r = await api.get('/api/conversas'); setThreads(r.data); } catch(e){} };
    const loadMsgs = async (t) => { try { const r = await api.get('/api/conversas/'+t.funcionario_id+'/'+t.telefone_contato); setMsgs(r.data); } catch(e){} };

    const openThread = async (t) => {
        setSel(t); setResults(null);
        await loadMsgs(t);
    };

    const doSearch = async () => { if(search.length<3) return; try { const r = await api.get('/api/conversas/busca/texto?q='+encodeURIComponent(search)); setResults(r.data); setSel(null); } catch(e){} };
    const fmt = (d) => { const dt = new Date(d); return dt.toLocaleDateString('pt-BR')+' '+dt.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}); };

    const universoAposFunc = useMemo(() => fFunc ? threads.filter(t => t.funcionario_nome === fFunc) : threads, [threads, fFunc]);
    const universoAposFuncECliente = useMemo(() => fCliente ? universoAposFunc.filter(t => t.cliente_nome === fCliente) : universoAposFunc, [universoAposFunc, fCliente]);

    const funcionariosDisponiveis = useMemo(() => {
        const base = fCliente ? threads.filter(t => t.cliente_nome === fCliente) : threads;
        return [...new Set(base.map(t => t.funcionario_nome))].filter(Boolean).sort();
    }, [threads, fCliente]);

    const clientesDisponiveis = useMemo(() => {
        const base = fFunc ? threads.filter(t => t.funcionario_nome === fFunc) : threads;
        return [...new Set(base.map(t => t.cliente_nome))].filter(Boolean).sort();
    }, [threads, fFunc]);

    const threadsFiltradas = useMemo(() => {
        let base = universoAposFuncECliente;
        if (fData) {
            const agora = new Date();
            let inicio = null, fim = null;
            if (fData === 'hoje') { inicio = new Date(agora); inicio.setHours(0,0,0,0); }
            else if (fData === '7d') { inicio = new Date(agora); inicio.setDate(inicio.getDate()-7); }
            else if (fData === '30d') { inicio = new Date(agora); inicio.setDate(inicio.getDate()-30); }
            else if (fData === 'mes') { inicio = new Date(agora.getFullYear(), agora.getMonth(), 1); }
            else if (fData === 'custom') {
                if (dtDe) inicio = new Date(dtDe + 'T00:00:00');
                if (dtAte) { fim = new Date(dtAte + 'T23:59:59'); }
            }
            base = base.filter(t => {
                const dt = new Date(t.ultima_mensagem);
                if (inicio && dt < inicio) return false;
                if (fim && dt > fim) return false;
                return true;
            });
        }
        return base;
    }, [universoAposFuncECliente, fData, dtDe, dtAte]);

    const temFiltroAtivo = fFunc || fCliente || fData;
    const limparFiltros = () => { setFFunc(''); setFCliente(''); setFData(''); setDtDe(''); setDtAte(''); };

    const isSaida = (d) => d === 'saida' || d === 'saida_humana';

    if (sel) {
        return (
            <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 80px)' }}>
                <div className="page-header" style={{ flexShrink:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, flex:1 }}>
                        <button className="btn-ghost" onClick={()=>setSel(null)} style={{ padding:'8px 10px' }}><ArrowLeft size={18}/></button>
                        <div style={{ flex:1 }}>
                            <h1 style={{ fontSize:20 }}>{sel.nome_contato?sel.nome_contato:sel.telefone_contato}{sel.nome_contato && <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:14, marginLeft:8 }}>· {sel.telefone_contato}</span>}</h1>
                            <p style={{ color:'var(--text-secondary)', fontSize:13 }}>{sel.funcionario_nome} - {sel.cliente_nome}</p>
                        </div>
                    </div>
                </div>

                <div style={{ flex:1, overflowY:'auto', padding:'16px 12px', display:'flex', flexDirection:'column', gap:6 }}>
                    {msgs.map((m,i) => (
                        <div key={m.id||i} style={{
                            display:'flex',
                            justifyContent: isSaida(m.direcao) ? 'flex-end' : 'flex-start'
                        }}>
                            <div style={{
                                maxWidth:'75%', padding:'8px 14px', borderRadius:12,
                                background: m.direcao==='saida_humana' ? 'rgba(245,158,11,0.15)'
                                    : isSaida(m.direcao) ? 'var(--purple-900)'
                                    : 'var(--bg-input)',
                                border: m.direcao==='saida_humana' ? '1px solid rgba(245,158,11,0.35)'
                                    : '1px solid var(--border)'
                            }}>
                                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                                    {m.direcao==='entrada'
                                        ? <User size={11} color="var(--green-400)"/>
                                        : m.direcao==='saida_humana'
                                        ? <UserCheck size={11} color="#f59e0b"/>
                                        : <Bot size={11} color="var(--purple-400)"/>}
                                    <span style={{ fontSize:11, color: m.direcao==='saida_humana' ? '#f59e0b' : 'var(--text-muted)' }}>
                                        {m.direcao==='entrada' ? (m.nome_contato||'Cliente')
                                            : m.direcao==='saida_humana' ? 'Humano'
                                            : 'IA'} - {fmt(m.criado_em)}
                                    </span>
                                </div>
                                <p style={{ fontSize:14, lineHeight:1.5, whiteSpace:'pre-wrap', color: m.tipo_mensagem!=='telefonia' ? 'var(--green-400)' : undefined }}>{m.conteudo}</p>
                                {m.audio_transcricao && <p style={{ fontSize:12, color:'var(--amber-500)', marginTop:4 }}>Transcricao de audio</p>}
                                {m.tokens_usados>0 && <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{m.tokens_usados} tokens - {m.tempo_resposta_ms}ms</p>}
                            </div>
                        </div>
                    ))}
                    {msgs.length===0 && <p style={{ color:'var(--text-muted)', textAlign:'center', padding:40 }}>Nenhuma mensagem</p>}
                    <div ref={msgsEndRef}/>
                </div>
            </div>
        );
    }

    const selectStyle = { padding:'10px 14px', borderRadius:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', color:'var(--text-primary)', fontSize:14, minWidth:170, cursor:'pointer' };
    const dateInputStyle = { padding:'10px 12px', borderRadius:8, background:'var(--bg-secondary)', border:'1px solid var(--border)', color:'var(--text-primary)', fontSize:14 };

    return (
        <div>
            <div className="page-header"><div><h1>Conversas</h1><p style={{ color:'var(--text-secondary)', fontSize:14, marginTop:4 }}>Historico de todas as interacoes</p></div></div>

            <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
                <Filter size={16} style={{ color:'var(--text-muted)' }}/>
                <select value={fFunc} onChange={e=>setFFunc(e.target.value)} style={selectStyle}>
                    <option value="">Todos funcionários</option>
                    {funcionariosDisponiveis.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={fCliente} onChange={e=>setFCliente(e.target.value)} style={selectStyle}>
                    <option value="">Todos clientes</option>
                    {clientesDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={fData} onChange={e=>setFData(e.target.value)} style={selectStyle}>
                    {DATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {fData === 'custom' && (
                    <>
                        <input type="date" value={dtDe} onChange={e=>setDtDe(e.target.value)} style={dateInputStyle}/>
                        <span style={{ color:'var(--text-muted)', fontSize:13 }}>até</span>
                        <input type="date" value={dtAte} onChange={e=>setDtAte(e.target.value)} style={dateInputStyle}/>
                    </>
                )}
                {temFiltroAtivo && (
                    <button onClick={limparFiltros} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:8, background:'transparent', border:'1px solid var(--border)', color:'var(--text-secondary)', fontSize:13, cursor:'pointer' }}>
                        <X size={14}/> Limpar filtros
                    </button>
                )}
            </div>

            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doSearch()} placeholder="Buscar no conteudo das mensagens..." style={{ maxWidth:400 }}/>
                <button className="btn-primary" onClick={doSearch} style={{ display:'flex', alignItems:'center', gap:6 }}><Search size={16}/> Buscar</button>
            </div>

            {results ? (
                <div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                        <p style={{ color:'var(--text-secondary)', fontSize:14 }}>{results.length} resultado(s)</p>
                        <button className="btn-ghost" onClick={()=>setResults(null)} style={{ fontSize:13 }}>Limpar busca</button>
                    </div>
                    <div className="card" style={{ padding:0 }}>
                        <table>
                            <thead><tr><th>Contato</th><th>Funcionario</th><th>Mensagem</th><th>Data</th></tr></thead>
                            <tbody>{results.map((m,i) => (
                                <tr key={i}><td>{m.nome_contato?m.nome_contato:m.telefone_contato}{m.nome_contato && <span style={{ color:'var(--text-muted)', marginLeft:6 }}>· {m.telefone_contato}</span>}</td><td style={{ color:'var(--text-secondary)' }}>{m.funcionario_nome}</td><td style={{ maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.conteudo}</td><td style={{ color:'var(--text-muted)', fontSize:13 }}>{fmt(m.criado_em)}</td></tr>
                            ))}</tbody>
                        </table>
                    </div>
                </div>
            ) : threads.length===0 ? (
                <div className="card" style={{ textAlign:'center', padding:60 }}>
                    <MessageSquare size={40} color="var(--text-muted)" style={{ marginBottom:16 }}/>
                    <p style={{ color:'var(--text-muted)' }}>Nenhuma conversa ainda</p>
                    <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:8 }}>As conversas aparecerao quando os funcionarios comecarem a atender</p>
                </div>
            ) : (
                <>
                    {temFiltroAtivo && (
                        <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:12 }}>{threadsFiltradas.length} de {threads.length} conversas</p>
                    )}
                    {threadsFiltradas.length === 0 ? (
                        <div className="card" style={{ textAlign:'center', padding:40 }}>
                            <p style={{ color:'var(--text-muted)' }}>Nenhuma conversa corresponde aos filtros</p>
                        </div>
                    ) : (
                        <div className="card" style={{ padding:0 }}>
                            <table>
                                <thead><tr><th>Contato</th><th>Funcionario</th><th>Cliente</th><th>Msgs</th><th>Ultima</th></tr></thead>
                                <tbody>{threadsFiltradas.map((t,i) => (
                                    <tr key={i} onClick={()=>openThread(t)} style={{ cursor:'pointer' }}>
                                        <td style={{ fontWeight:500 }}>{t.nome_contato?t.nome_contato:t.telefone_contato}{t.nome_contato && <span style={{ color:'var(--text-muted)', fontWeight:400, marginLeft:6 }}>· {t.telefone_contato}</span>}</td>
                                        <td><span className="badge badge-purple">{t.funcionario_nome}</span></td>
                                        <td style={{ color:'var(--text-secondary)' }}>{t.cliente_nome}</td>
                                        <td>{t.total_mensagens}</td>
                                        <td style={{ color:'var(--text-muted)', fontSize:13 }}>{fmt(t.ultima_mensagem)}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
